import {useQuery, UseQueryOptions} from '@tanstack/react-query';
import {MethodId} from '~/entities/operations-fiat';
import {expectEither, HttpClient, POST, useHttpClient} from '~/library/network-facade';
import {AccountId, BankId, Outcome} from '~/library/types';
import {useShallowDebounce} from '~/library/ui-kit';
import {FeeData} from "../../model/FeeData";

async function fetchWithdrawFee(
    http: HttpClient,
    accountId: AccountId,
    methodId: MethodId | undefined,
    amount: string
) {
    return http(
        `/v1/accounts/${accountId}/send/banks/${methodId}/fee`,
        POST({
            amount,
        })
    ).then(
        ...expectEither({
            200: FeeData,
        })
    );
}

type WithdrawFeeInput = {
    accountId: AccountId;
    methodId: BankId | undefined;
    amount: string;
};

export function useWithdrawFeeOptions({
    accountId,
    methodId,
    amount,
}: WithdrawFeeInput): UseQueryOptions<Outcome<FeeData | null, never>> {
    const http = useHttpClient();

    return {
        queryKey: ['v1/accounts/{accountId}/send/banks/{paymentNetworkId}/fee', accountId, methodId, amount],
        queryFn: async ({client, queryKey}) => {
            if (!methodId) {
                return {
                    value: null,
                };
            }

            const res = await fetchWithdrawFee(http, accountId, methodId, amount);

            switch (res.status) {
                case 200:
                    return {value: res.json};
                case 'Abort': {
                    await client.cancelQueries({queryKey});
                    throw res.reason;
                }
            }
        },
        enabled: !!methodId,
    };
}

export function useWithdrawFee(input: WithdrawFeeInput) {
    const {isDebouncing, value} = useShallowDebounce(200, input);
    const queryOptions = useWithdrawFeeOptions(value);
    const query = useQuery(queryOptions);
    return {...query, isDebouncing};
}
